package com.example.mystybag.core.logic.base

import androidx.compose.ui.text.input.ImeAction
import com.arkivanov.decompose.value.MutableValue
import com.arkivanov.decompose.value.Value
import com.arkivanov.decompose.value.update
import com.example.mystybag.core.defines.UtilsActionsDefines.ACTION_NONE
import com.example.mystybag.core.logic.helpers.NavigationBarConfig
import com.example.mystybag.core.model.UINavigationCallbacks
import com.example.mystybag.core.model.UINavigationState
import com.example.mystybag.core.model.UISearchCallbacks
import com.example.mystybag.core.model.UISearchTextFieldState
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.FlowCollector
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.mapLatest

abstract class ChildViewModel<T: Any>(
    context             : AppContext,
    uiData              : T,
    baseAction          : Int       = ACTION_NONE
): BaseViewModel(context, baseAction) {
    private val navigationState         : MutableValue<UINavigationState>       = MutableValue(UINavigationState())
    private val state                   : MutableValue<T>                       = MutableValue(uiData)

    val uiState     : Value<T>  = state
    val navControls             = NavigationBarConfig(navigationState)

    val searchControls = object: UISearchCallbacks {
        override val state : UISearchTextFieldState  = UISearchTextFieldState()

        override fun onSearchTextChanged(search: String) {
            state.searchText.value = search
            this@ChildViewModel.onSearchTextChanged(search)
        }

        override fun onKeyboardAction(imeAction: ImeAction): Boolean {
            return this@ChildViewModel.onKeyboardAction(imeAction)
        }

        override var openSearch: Boolean
            get()       = state.openSearch.value
            set(value)  { state.openSearch.value = value }

        override var searchText: String
            get()       = state.searchText.value
            set(value)  { state.searchText.value = value }
    }

    val navCallbacks = object: UINavigationCallbacks {
        override val controls: Value<UINavigationState> = this@ChildViewModel.navigationState

        override fun backButtonCallback() {
            this@ChildViewModel.onBackButtonClicked()
        }
    }

    fun updateState(action: (T) -> T) = state.update(action)

    open fun onSearchTextChanged(search: String) {}


    /**
     * A flow of search string derived from the current user input.
     *
     * This flow emits values with the following behavior:
     * - If the input is empty, it emits immediately.
     * - Otherwise, it applies a 350ms delay to reduce unnecessary emissions during fast typing.
     * - It filters out consecutive duplicate values using [Flow.distinctUntilChanged].
     */
    @OptIn(FlowPreview::class)
    protected val searchFlow: Flow<String>
        get() = searchControls.state.searchText
            .debounce {
                if (it.isEmpty()) 0L else 350L
            }
            .distinctUntilChanged()

    /**
     * This function listens for emissions from the [searchFlow], calls the provided [transform] function
     * passing the new search string, and emits the returned result to the given [collector].
     * If a new value is emitted by [searchFlow] before the previous [transform] completes, the previous
     * transformation is cancelled.
     *
     * This function is mostly used to filter lists, observed in Compose.
     *
     * Example: Filter the list of operators with name containing the input from the user
     * ```
     * var allOperators = listOf<GamblingOperatorVisualModel>()
     * val operators    = MutableStateFlow<List<GamblingOperatorVisualModel>>(listOf())
     *
     * scope.launch {
     *     filterSearch(operators) { search ->
     *         if (search.isEmpty())
     *             allOperators
     *         else
     *             allOperators.filter { operator ->
     *                 operator.name.contains(search, ignoreCase = true)
     *             }
     *     }
     * }
     * ```
     * @param collector The [FlowCollector] that will receive the transformed results.
     * @param transform A function that defines how to transform the search string.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    protected suspend fun <T> filterSearch(collector: FlowCollector<T>, transform: suspend (String) -> T) {
        searchFlow
            .mapLatest(transform)
            .collect(collector)
    }
}

